// Modale « Nouvelle tâche » (WKF-013 §7.3). Pattern maison : useState + toast
// + mutation TanStack (le repo n'utilise NI react-hook-form NI zod).

import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Check } from 'lucide-react'

import { Button } from '@/components/ui/button'
import {
    Command,
    CommandEmpty,
    CommandInput,
    CommandItem,
    CommandList,
} from '@/components/ui/command'
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { SimpleRichText } from '@/components/ui/simple-rich-text'
import { useAuth } from '@/contexts/AuthContext'
import {
    notificationErrorMessage,
    useNotificationMutations,
} from '@/hooks/useNotifications'
import type { Priority } from '@/hooks/useNotifications.types'
import { linkedEntityHref, type LinkedEntityValue } from '@/lib/linkedEntity'
import { sanitizeRichHtml } from '@/lib/richText'
import { cn } from '@/lib/utils'

import { ColleagueAvatar } from './ColleagueAvatar'
import { DueDatePicker } from './DueDatePicker'
import { LinkedEntityPicker } from './LinkedEntityPicker'
import { PrioritySelect } from './PrioritySelect'
import { useDestinatairesDeLaTache } from './useDestinatairesDeLaTache'

interface TaskCreateDialogProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    entityId: string | null
    linkedEntityType?: string | null
    linkedEntityId?: string | null
    linkedLabel?: string | null
    /** Échéance pré-remplie (ISO) — ex. création depuis une case du calendrier. */
    initialDueDate?: string | null
    /**
     * Titre pré-rempli — et il reste MODIFIABLE.
     *
     * 🔴 Le titre est OBLIGATOIRE (`submit` refuse un titre vide, et le bouton
     * reste désactivé). Une tâche ouverte depuis un objet daté — une échéance
     * d'appel d'offres — arrivait donc vide : l'utilisateur devait retaper ce
     * que l'écran savait déjà, et rien dans la tâche ne disait plus de quelle
     * échéance elle était née.
     *
     * Strictement ADDITIF, dans l'esprit exact d'`initialDueDate` : un appelant
     * qui ne le passe pas retrouve le champ vide, exactement comme avant.
     */
    initialTitle?: string | null
    /**
     * Appelé APRÈS création, avec l'identifiant de la tâche née ET le lien retenu.
     *
     * 🔴 Sans l'identifiant, un appelant qui doit RATTACHER la tâche à l'objet
     * d'où elle part — une échéance d'appel d'offres — ne savait pas laquelle
     * venait de naître. Sans le lien : la modale laisse CHANGER ou RETIRER la
     * fiche liée, et seule une tâche liée à l'AO se rattache à son échéance
     * (PO n° 66) — l'appelant juge sur ce lien, pas sur celui qu'il a proposé.
     *
     * Ajout PUREMENT additif : les appelants qui ignorent les arguments compilent
     * et se comportent exactement comme avant.
     */
    onCreated?: (taskId: string, lien: LinkedEntityValue | null) => void
}

export function TaskCreateDialog({
    open,
    onOpenChange,
    entityId,
    linkedEntityType,
    linkedEntityId,
    linkedLabel,
    initialDueDate,
    initialTitle,
    onCreated,
}: TaskCreateDialogProps) {
    const { t } = useTranslation()
    const { hasPermission } = useAuth()
    const { createTask } = useNotificationMutations(entityId)

    const [title, setTitle] = useState('')
    const [message, setMessage] = useState('')
    const [priority, setPriority] = useState<Priority>('NORMAL')
    const [dueDate, setDueDate] = useState<string | null>(null)
    const [assignToOther, setAssignToOther] = useState(false)
    const [assigneeId, setAssigneeId] = useState<string>('')

    // Lien pré-rempli depuis le contexte d'ouverture (ex. fiche client) — l'URL
    // est dérivée du type/id. Le picker permet ensuite de le changer ou le retirer.
    const initialLink = useMemo<LinkedEntityValue | null>(
        () =>
            linkedEntityType && linkedEntityId && linkedLabel
                ? {
                      type: linkedEntityType,
                      id: linkedEntityId,
                      label: linkedLabel,
                      href: linkedEntityHref(linkedEntityType, linkedEntityId) ?? '',
                  }
                : null,
        [linkedEntityType, linkedEntityId, linkedLabel],
    )
    const [linkedValue, setLinkedValue] = useState<LinkedEntityValue | null>(initialLink)

    // Collègues candidats (session, « derniers utilisés » d'abord ; lien d'AO : ceux qui voient l'AO,
    // PO n° 66). Part le choix ENCORE proposé (`retenu`), jamais un collègue que le lien a sorti.
    const { ordered: colleagues, remember, ...destinataires } = useDestinatairesDeLaTache(entityId, linkedValue)
    const canAssign = hasPermission('tasks:assign') && destinataires.aDesCollegues
    const choisi = canAssign && assignToOther ? destinataires.retenu(assigneeId) : ''

    useEffect(() => {
        if (open) {
            // `?? ''` et non `|| ''` : seuls `null`/`undefined` retombent sur le vide — un appelant
            // qui n'en passe pas retrouve le champ vide, comme avant (test de non-régression).
            setTitle(initialTitle ?? '')
            setMessage('')
            setPriority('NORMAL')
            setDueDate(initialDueDate ?? null)
            setAssignToOther(false)
            setAssigneeId('')
            setLinkedValue(initialLink)
        }
    }, [open, initialDueDate, initialTitle, initialLink])

    const submitting = createTask.isPending

    async function submit() {
        if (!title.trim()) {
            toast.error(t('notifications.task.titleRequired', 'Donnez un titre à votre tâche.'))
            return
        }
        try {
            const creee = await createTask.mutateAsync({
                title: title.trim(),
                // Le message est du HTML (SimpleRichText) : on le sanitise AVANT
                // persistance (allowlist) → aucune donnée dangereuse stockée.
                message: sanitizeRichHtml(message) || null,
                priority,
                due_date: dueDate,
                recipient_id: choisi || null,
                linked_entity_type: linkedValue?.type ?? null,
                linked_entity_id: linkedValue?.id ?? null,
                linked_entity_label: linkedValue?.label ?? null,
                // `|| null` et non `?? null` : un href VIDE (type absent du registre
                // de recherche → `linkedEntityHref` rend `null`, remplacé par `''`)
                // est REFUSÉ par le validateur anti-open-redirect du serveur — 422
                // avant la route, sans log, et la tâche n'est pas créée. Une chaîne
                // vide veut dire « pas d'URL », pas « URL invalide ».
                linked_entity_href: linkedValue?.href || null,
            })
            if (choisi) remember(choisi)
            toast.success(t('notifications.task.created', 'Tâche créée.'))
            onCreated?.(creee.id, linkedValue)
            onOpenChange(false)
        } catch (err) {
            toast.error(notificationErrorMessage(err, t))
        }
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[560px]">
                <DialogHeader>
                    <DialogTitle>{t('notifications.task.newTitle', 'Nouvelle tâche')}</DialogTitle>
                </DialogHeader>

                {/* min-w-0 : ce div est un item de la grille `DialogContent`
                    (min-width:auto par défaut) ; sans lui, un enfant large — dont
                    une « Fiche liée » au libellé long — refuse de rétrécir, gonfle
                    la piste et fait rogner/décaler le contenu à droite. Cf.
                    pitfall_dialog_mobile_overflow_x_layout_viewport. */}
                <div className="min-w-0 space-y-4 py-2">
                    <div className="space-y-1.5">
                        <Label htmlFor="task-title">
                            {t('notifications.task.fieldTitle', 'Titre')} *
                        </Label>
                        <Input
                            id="task-title"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            maxLength={255}
                            autoFocus
                            placeholder={t(
                                'notifications.task.titlePlaceholder',
                                'Rappeler ACME pour confirmer les stagiaires',
                            )}
                        />
                    </div>

                    <div className="space-y-1.5">
                        <Label>{t('notifications.task.fieldDescription', 'Description')}</Label>
                        {/* Éditeur non contrôlé → remonté à chaque ouverture (key) pour
                            repartir vide. onChange tient `message` à jour pour le submit. */}
                        <SimpleRichText
                            key={open ? 'open' : 'closed'}
                            defaultValue=""
                            onChange={setMessage}
                            rows={3}
                            placeholder={t(
                                'notifications.task.descriptionPlaceholder',
                                'Détails, contexte, points à vérifier…',
                            )}
                        />
                    </div>

                    {canAssign && (
                        <div className="space-y-1.5">
                            <Label>{t('notifications.task.assignee', 'Assignée à')}</Label>
                            <RadioGroup
                                value={assignToOther ? 'other' : 'me'}
                                onValueChange={(v) => setAssignToOther(v === 'other')}
                                className="flex gap-4"
                            >
                                <div className="flex items-center gap-2">
                                    <RadioGroupItem value="me" id="assignee-me" />
                                    <Label htmlFor="assignee-me" className="font-normal">
                                        {t('notifications.task.assigneeMe', 'Moi-même')}
                                    </Label>
                                </div>
                                <div className="flex items-center gap-2">
                                    <RadioGroupItem value="other" id="assignee-other" />
                                    <Label htmlFor="assignee-other" className="font-normal">
                                        {t('notifications.task.assigneeColleague', 'Un collègue')}
                                    </Label>
                                </div>
                            </RadioGroup>
                            {assignToOther && (
                                <Command className="rounded-md border">
                                    <CommandInput
                                        placeholder={t('notifications.transfer.searchPlaceholder', 'Rechercher un collègue…')}
                                    />
                                    <CommandList className="max-h-52">
                                        <CommandEmpty>
                                            {t('notifications.transfer.noColleague', 'Aucun collègue trouvé.')}
                                        </CommandEmpty>
                                        {colleagues.map((u) => {
                                            const label = u.full_name || u.email
                                            return (
                                                <CommandItem
                                                    key={u.id}
                                                    value={`${label} ${u.email}`}
                                                    onSelect={() => setAssigneeId(u.id)}
                                                >
                                                    <ColleagueAvatar colleague={u} />
                                                    <span className="truncate">{label}</span>
                                                    <Check
                                                        className={cn(
                                                            'ml-auto h-4 w-4',
                                                            choisi === u.id ? 'opacity-100' : 'opacity-0',
                                                        )}
                                                    />
                                                </CommandItem>
                                            )
                                        })}
                                    </CommandList>
                                </Command>
                            )}
                        </div>
                    )}

                    <div className="space-y-1.5">
                        <Label>{t('notifications.task.due', 'Échéance')}</Label>
                        <DueDatePicker value={dueDate} onChange={setDueDate} />
                    </div>

                    <div className="space-y-1.5">
                        <Label>{t('notifications.task.priority', 'Priorité')}</Label>
                        <PrioritySelect value={priority} onChange={setPriority} />
                    </div>

                    <LinkedEntityPicker
                        value={linkedValue}
                        onChange={setLinkedValue}
                        entityId={entityId}
                    />
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
                        {t('common.cancel', 'Annuler')}
                    </Button>
                    <Button onClick={submit} disabled={submitting || !title.trim() || (assignToOther && canAssign && !choisi)}>
                        {t('notifications.task.create', 'Créer')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}
